/**
 * Cart page Budget Pay radios.
 * Reads the installment count and selling plan id already printed by
 * snippets/cart-line-budget-pay-choice.liquid, then updates that cart line.
 */

import { fetchCartJson, seedCartCache } from '@theme/cart-api';
import { ThemeEvents } from '@theme/events';
import { alignCartBpTierFromCart } from '@theme/budget-pay-cart-align';
import {
  appendSectionsToBody,
  refreshCartPageAfterMutationWithFallback,
} from '@theme/cart-section-refresh';
import {
  BP_MIN_PROPERTY_KEY,
  BP_PLANS_PROPERTY_KEY,
  computeBpExposureCents,
  DEFAULT_BP_GUEST_LIMIT,
  isBudgetPayCartLine,
  validateEligibility,
} from '@theme/budget-pay-rules';

const BP_PROPERTY_KEYS = [BP_MIN_PROPERTY_KEY, BP_PLANS_PROPERTY_KEY];

let pending = false;

function showLoader() {
  if (typeof window.showTjcLoader === 'function') window.showTjcLoader();
}

function setLoaderProgress(percent) {
  if (typeof window.setTjcLoaderProgress === 'function') window.setTjcLoaderProgress(percent);
}

function hideLoader() {
  if (typeof window.hideTjcLoader === 'function') window.hideTjcLoader();
}

function parseBooleanOrDefault(value, defaultValue) {
  if (value == null || value === '') return defaultValue;
  const normalized = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n'].includes(normalized)) return false;
  return defaultValue;
}

function parseNumberOrDefault(value, defaultValue) {
  if (value == null || value === '') return defaultValue;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return defaultValue;
  return parsed;
}

function getBpEligibilityFromBody() {
  const d = document.body?.dataset ?? {};
  const guestLimit = parseNumberOrDefault(d.bpGuestLimit, DEFAULT_BP_GUEST_LIMIT);
  const available = parseBooleanOrDefault(d.bpAvailable, true);
  const creditLimit = parseNumberOrDefault(d.bpCreditLimit, guestLimit);
  return { available, creditLimit };
}

function propertiesToObject(properties) {
  if (!properties) return {};
  if (Array.isArray(properties)) {
    /** @type {Record<string, string>} */
    const fromList = {};
    for (const entry of properties) {
      const key = entry?.name ?? entry?.key;
      if (!key || entry?.value == null) continue;
      fromList[String(key)] = String(entry.value);
    }
    return fromList;
  }
  if (typeof properties !== 'object') return {};

  /** @type {Record<string, string>} */
  const out = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value == null) continue;
    out[key] = typeof value === 'string' ? value : JSON.stringify(value);
  }
  return out;
}

function withoutBudgetPayProperties(properties) {
  const next = { ...properties };
  for (const key of BP_PROPERTY_KEYS) delete next[key];
  return next;
}

/**
 * @param {HTMLElement} group
 * @param {string} message
 */
function showChoiceError(group, message) {
  const card = group.closest('.tjc-product-card');
  const error = card?.querySelector('.cart-page__item-bp-error');
  const errorText = card?.querySelector('.cart-page__item-bp-error-text');
  if (error instanceof HTMLElement && errorText instanceof HTMLElement) {
    errorText.textContent = message;
    error.hidden = false;
    error.classList.remove('hidden');
    return;
  }
  window.alert(message);
}

/** @param {HTMLElement} group */
function clearChoiceError(group) {
  const card = group.closest('.tjc-product-card');
  const error = card?.querySelector('.cart-page__item-bp-error');
  if (!(error instanceof HTMLElement)) return;
  error.hidden = true;
  error.classList.add('hidden');
}

/** @param {HTMLElement} group */
function revertChoice(group) {
  const selected = group.dataset.selected === 'bp' ? 'bp' : 'full';
  const input = group.querySelector(`input[value="${selected}"]`);
  if (input instanceof HTMLInputElement) input.checked = true;
}

function setChoicesDisabled(disabled) {
  document.querySelectorAll('[data-cart-bp-choice] input').forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    if (input.dataset.bpLocked === 'true') {
      input.disabled = true;
      return;
    }
    input.disabled = disabled;
  });
}

/**
 * @param {object} cart
 * @param {object} liveItem
 * @param {number} lineIndex
 * @param {number} fullUnitCents
 * @param {number} minInstallments
 * @param {string} plansJson
 */
function assertCanEnableBudgetPay(cart, liveItem, lineIndex, fullUnitCents, minInstallments, plansJson) {
  const { available, creditLimit } = getBpEligibilityFromBody();
  if (!available) {
    throw new Error('Budget Pay is not available for your account.');
  }

  const projected = {
    ...liveItem,
    properties: {
      ...propertiesToObject(liveItem.properties),
      [BP_MIN_PROPERTY_KEY]: String(minInstallments),
      [BP_PLANS_PROPERTY_KEY]: plansJson,
    },
    selling_plan_allocation: {
      selling_plan: { name: `Pay in ${minInstallments}` },
      compare_at_price: fullUnitCents,
    },
    final_price: Math.round(fullUnitCents / Math.max(minInstallments, 1)),
  };

  const otherLines = (cart?.items ?? []).filter((item, index) => index !== lineIndex && isBudgetPayCartLine(item));
  const exposure = computeBpExposureCents([...otherLines, projected]);
  if (!exposure.ok) {
    throw new Error(exposure.message || 'Could not check the Budget Pay limit.');
  }

  const validation = validateEligibility({ available, creditLimit }, exposure.exposureCents);
  if (!validation.ok) {
    throw new Error(validation.message);
  }
}

/**
 * @param {number} lineNumber
 * @param {number} quantity
 * @param {string | number} sellingPlan
 * @param {Record<string, string>} properties
 */
async function changeCartLinePayment(lineNumber, quantity, sellingPlan, properties) {
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(
      appendSectionsToBody({
        line: lineNumber,
        quantity,
        selling_plan: sellingPlan,
        properties,
      }),
    ),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.description || data?.message || `Cart update failed (${res.status})`;
    throw new Error(msg);
  }
  seedCartCache(data);
  return data;
}

/**
 * @param {HTMLInputElement} input
 */
async function onChoiceChange(input) {
  const group = input.closest('[data-cart-bp-choice]');
  if (!(group instanceof HTMLElement) || pending) {
    if (group instanceof HTMLElement) revertChoice(group);
    return;
  }

  const mode = input.value === 'bp' ? 'bp' : 'full';
  const selected = group.dataset.selected === 'bp' ? 'bp' : 'full';
  if (mode === selected) return;

  const lineKey = group.dataset.lineKey || '';
  const minInstallments = parseInt(group.dataset.minInstallments || '', 10);
  const planId = parseInt(group.dataset.planId || '', 10);
  const fullUnitCents = parseInt(group.dataset.fullUnitCents || '', 10);
  const plansEl = group.querySelector('[data-bp-plans]');
  const plansJson = (plansEl?.textContent || '').trim();

  if (!lineKey || !Number.isFinite(minInstallments) || minInstallments < 2) {
    revertChoice(group);
    showChoiceError(group, 'Budget Pay is not available for this item.');
    return;
  }

  pending = true;
  clearChoiceError(group);
  setChoicesDisabled(true);
  showLoader();

  try {
    setLoaderProgress(20);
    const cart = await fetchCartJson({ force: true });
    const lineIndex = (cart?.items ?? []).findIndex((item) => item?.key === lineKey);
    const liveItem = lineIndex >= 0 ? cart.items[lineIndex] : null;
    if (!liveItem) {
      throw new Error('Could not find this item. Refresh the page and try again.');
    }

    const existingProperties = propertiesToObject(liveItem.properties);
    /** @type {Record<string, string>} */
    let properties;
    /** @type {string | number} */
    let sellingPlan;

    if (mode === 'bp') {
      if (!Number.isFinite(planId) || planId <= 0 || !plansJson || !Number.isFinite(fullUnitCents) || fullUnitCents <= 0) {
        throw new Error('No Budget Pay plan is available for this item.');
      }
      let plansMap;
      try {
        plansMap = JSON.parse(plansJson);
      } catch {
        plansMap = null;
      }
      if (plansMap == null || typeof plansMap !== 'object' || Array.isArray(plansMap)) {
        throw new Error('No Budget Pay plan is available for this item.');
      }
      const compactPlans = JSON.stringify(plansMap);
      assertCanEnableBudgetPay(cart, liveItem, lineIndex, fullUnitCents, minInstallments, compactPlans);
      properties = {
        ...existingProperties,
        [BP_MIN_PROPERTY_KEY]: String(minInstallments),
        [BP_PLANS_PROPERTY_KEY]: compactPlans,
      };
      sellingPlan = planId;
    } else {
      properties = withoutBudgetPayProperties(existingProperties);
      sellingPlan = '';
    }

    setLoaderProgress(45);
    let cartData = await changeCartLinePayment(lineIndex + 1, Number(liveItem.quantity) || 1, sellingPlan, properties);

    setLoaderProgress(65);
    try {
      const { cart: alignedCart, didMutate } = await alignCartBpTierFromCart(cartData);
      if (didMutate) cartData = alignedCart;
    } catch (alignErr) {
      console.error('[BudgetPay] cart tier align after payment choice', alignErr);
    }

    setLoaderProgress(80);
    await refreshCartPageAfterMutationWithFallback(cartData, {
      action: 'update',
      source: 'cart-page-budget-pay-choice',
      skipFreeGiftSync: true,
    });
  } catch (error) {
    revertChoice(group);
    const message = error instanceof Error ? error.message : 'Could not update Budget Pay.';
    showChoiceError(group, message);
    console.error('[BudgetPay] cart payment choice', error);
  } finally {
    pending = false;
    setChoicesDisabled(false);
    hideLoader();
  }
}

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement)) return;
  if (input.type !== 'radio' || !input.closest('[data-cart-bp-choice]')) return;
  void onChoiceChange(input);
});

/**
 * Shopify money format from the cart line (`£{{amount}}` and the common amount tokens).
 * @param {number} cents
 * @param {string} moneyFormat
 */
function formatCartMoney(cents, moneyFormat) {
  const rounded = Math.round(cents);
  const amount = (rounded / 100).toFixed(2);
  const format = moneyFormat || '£{{amount}}';
  return format
    .replace(/\{\{\s*amount\s*\}\}/g, amount)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(rounded / 100)))
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
    .replace(/\{\{\s*amount_without_trailing_zeros\s*\}\}/g, String(parseFloat(amount)));
}

/**
 * Price column is unit price × quantity. Paint it before /cart/change.js returns.
 * @param {HTMLElement} row
 * @param {number} quantity
 */
function paintCartLineExtendedPrice(row, quantity) {
  const qty = Math.max(0, Math.floor(quantity));

  const choice = row.querySelector('[data-cart-bp-choice]');
  if (choice instanceof HTMLElement) {
    const unitFull = parseInt(choice.dataset.fullUnitCents || '', 10);
    const unitEmi = parseInt(choice.dataset.unitEmiCents || '', 10);
    const format = choice.dataset.moneyFormat || '';
    const fullEl = choice.querySelector('[data-bp-full-amount]');
    const emiEl = choice.querySelector('[data-bp-emi-amount]');
    if (fullEl && Number.isFinite(unitFull)) {
      fullEl.textContent = formatCartMoney(unitFull * qty, format);
    }
    if (emiEl && Number.isFinite(unitEmi)) {
      emiEl.textContent = formatCartMoney(unitEmi * qty, format);
    }
  }

  const plain = row.querySelector('[data-cart-line-price]');
  if (plain instanceof HTMLElement) {
    const unit = parseInt(plain.dataset.unitCents || '', 10);
    const amountEl = plain.querySelector('[data-cart-line-price-amount]');
    if (amountEl && Number.isFinite(unit)) {
      amountEl.textContent = formatCartMoney(unit * qty, plain.dataset.moneyFormat || '');
    }
  }
}

/**
 * @param {number} line
 * @param {number} quantity
 */
function paintCartLineExtendedPriceByLine(line, quantity) {
  const input = document.querySelector(
    `cart-quantity-selector-component input[data-cart-line="${line}"]`,
  );
  const row =
    input instanceof HTMLElement
      ? input.closest('.tjc-product-card')
      : document.querySelector(`.tjc-product-card[data-cart-line="${line}"]`);
  if (row instanceof HTMLElement) paintCartLineExtendedPrice(row, quantity);
}

window.paintCartLineExtendedPrice = paintCartLineExtendedPriceByLine;

document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  const input = event.target;
  if (!(input instanceof HTMLElement)) return;
  const row = input.closest('.tjc-product-card');
  if (!(row instanceof HTMLElement)) return;
  const quantity = Number(event.detail?.quantity);
  if (!Number.isFinite(quantity) || quantity < 1) return;
  paintCartLineExtendedPrice(row, quantity);
});
